import { computePosition, flip, offset, Placement } from '@floating-ui/dom'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getNodeByKey, NodeKey, NodeMutation } from 'lexical'
import { useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Position } from '../../type'
import { useSpEditorProvider } from '../SpEditorProvider'
import SpSuggestionBox from '../SpSuggestionBox'
import { BaseNode } from '../nodes/BaseNode'
import { CharacterNode } from '../nodes/CharacterNode'
import { SceneNode } from '../nodes/SceneNode'
import { TransitionNode } from '../nodes/TransitionNode'
import { getCurrentNode } from '../utils/NodeUtils'
import { KEYS } from '@/components/custom/editor/stringKeys/StringKeys'
export type TSuggestionProps<T extends { title: string }> = {
    coords: Position | undefined
    options: T[]
}
export function SpSuggestionMenuPlugin() {
    const ref = useRef<HTMLDivElement>(null)
    const { setSuggestionProps, suggestionProps } = useSpEditorProvider()
    const [editor] = useLexicalComposerContext()

    const calculatePosition = useCallback(
        (key: NodeKey) => {
            const doPositioning = async () => {
                const domElem = editor.getElementByKey(key)
                const floatingElem = ref.current
                let coords: Position | undefined = undefined
                if (domElem && floatingElem) {
                    const currentNode = editor.read(() => getCurrentNode() as BaseNode)
                    try {
                        const pos = await computePosition(domElem, floatingElem, {
                            middleware: [offset(0), flip()],
                            placement: currentNode?.suggestionPlacement as Placement,
                        })

                        coords = { left: pos.x, top: pos.y }
                    } catch {
                        console.log('error')
                    }
                    setSuggestionProps((prev) => ({
                        ...prev,
                        coords,
                    }))
                }
            }
            setTimeout(doPositioning, 0)
        },
        [editor]
    )

    const mutationCallback = (mutations: Map<NodeKey, NodeMutation>) => {
        mutations.forEach((mutation, key) => {
            editor.getEditorState().read(() => {
                const node = getCurrentNode() as BaseNode
                const nodeByKey = $getNodeByKey(key)
                const canShow = canShowSuggestion(nodeByKey as BaseNode, node, mutation)
                if (canShow) {
                    calculatePosition(key)
                } else {
                    setSuggestionProps((prev) => ({
                        ...prev,
                        coords: undefined,
                    }))
                }
                return node
            })
        })
    }

    const canShowSuggestion = (
        mutationNode: BaseNode | null,
        currentNode: BaseNode | null,
        mutation: string
    ): boolean => {
        if (mutationNode == null || currentNode == null) return false
        if (currentNode?.enableSuggestions === false) return false
        if (mutation !== 'created') return false
        if (mutationNode.getType() != currentNode.getType()) return false
        // Don't show suggestion if there is content
        if (currentNode.isNotEmpty()) return false
        return true
    }
    const nodes = [CharacterNode, SceneNode, TransitionNode]

    useEffect(() => {
        const unregisterFns = nodes.map((node) =>
            editor.registerMutationListener(node, mutationCallback)
        )
        return () => {
            unregisterFns.forEach((fn) => fn())
        }
    }, [editor])

    const dom = document.getElementById(KEYS.SP_EDITOR_CONTAINER)
    if (!dom) return null
    return createPortal(
        <SpSuggestionBox ref={ref} editor={editor} position={suggestionProps.coords!} />,
        dom
    )
}
